import { useEffect, useMemo, useRef, useState } from "react";
import {
  CheckCircle2,
  XCircle,
  Sparkles,
  Brain,
  Lightbulb,
  Highlighter,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  parseExplanation,
  countMatches,
  highlightMatches,
  SEARCH_MIN_LEN,
  type ParsedSection,
  type SectionKind,
} from "@/lib/explanationFormat";
import {
  getGlobalExpandPreference,
  setGlobalExpandPreference,
  getHighlights,
  setHighlights,
  type HighlightSource,
} from "@/lib/explanationUiState";

interface ExplanationBodyProps {
  text: string;
  /** Required to persist highlights and expand preference per question. */
  mcqId?: string;
  /** Distinguishes original vs advanced vs flashcard for highlight scoping. */
  source?: HighlightSource;
  compact?: boolean;
  className?: string;
  /** Live search query. When set + active, matches are highlighted and non-matching blocks dim. */
  searchQuery?: string;
  /** Reports total match count across all blocks back to the parent. */
  onMatchCountChange?: (count: number) => void;
}

const SECTION_META: Record<
  SectionKind,
  { icon: typeof CheckCircle2; iconClass: string; accentClass: string }
> = {
  correct: {
    icon: CheckCircle2,
    iconClass: "text-success",
    accentClass: "border-l-success/60 bg-success/5",
  },
  incorrect: {
    icon: XCircle,
    iconClass: "text-destructive",
    accentClass: "border-l-destructive/50 bg-destructive/5",
  },
  pearl: {
    icon: Sparkles,
    iconClass: "text-primary",
    accentClass: "border-l-primary/50 bg-primary/5",
  },
  mechanism: {
    icon: Brain,
    iconClass: "text-primary",
    accentClass: "border-l-primary/40 bg-muted/40",
  },
};

/* ---------------- Block shell with highlight marker + search dimming ---------------- */

interface BlockShellProps {
  blockId: string;
  highlighted: boolean;
  onToggleHighlight: ((id: string) => void) | null;
  baseClass: string;
  compact: boolean;
  /** When search is active, true = block contains a match; false = dim. */
  searchDimmed: boolean;
  children: React.ReactNode;
}

function BlockShell({
  blockId,
  highlighted,
  onToggleHighlight,
  baseClass,
  compact,
  searchDimmed,
  children,
}: BlockShellProps) {
  return (
    <section
      className={cn(
        "group relative rounded-lg border-l-4 border border-border/50 transition-all",
        baseClass,
        highlighted &&
          "!bg-highlight-bg !border-l-highlight ring-1 ring-highlight/40",
        searchDimmed && "opacity-40",
        compact ? "p-3 sm:p-3.5" : "p-4"
      )}
    >
      {onToggleHighlight && (
        <button
          type="button"
          onClick={() => onToggleHighlight(blockId)}
          aria-pressed={highlighted}
          aria-label={highlighted ? "Remove highlight" : "Highlight this block"}
          className={cn(
            "absolute top-2 right-2 inline-flex items-center justify-center rounded-md",
            "h-7 w-7 text-muted-foreground hover:text-foreground hover:bg-muted/60",
            "transition-opacity focus-visible:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
            "[@media(hover:hover)]:opacity-0 group-hover:opacity-100",
            highlighted && "!opacity-100 text-highlight-foreground hover:text-highlight-foreground"
          )}
        >
          <Highlighter
            className={cn("h-4 w-4", highlighted && "fill-highlight text-highlight")}
            aria-hidden
          />
        </button>
      )}
      {children}
    </section>
  );
}

/* ---------------- Sub-renderers ---------------- */

function Paragraphs({
  paragraphs,
  textClass,
  spacing,
  searchQuery,
}: {
  paragraphs: string[];
  textClass: string;
  spacing: string;
  searchQuery: string;
}) {
  return (
    <div className={spacing}>
      {paragraphs.map((p, i) => (
        <p key={i} className={cn("leading-relaxed whitespace-pre-wrap", textClass)}>
          {searchQuery ? highlightMatches(p, searchQuery) : p}
        </p>
      ))}
    </div>
  );
}

function SectionContent({
  section,
  compact,
  searchQuery,
}: {
  section: ParsedSection;
  compact: boolean;
  searchQuery: string;
}) {
  const meta = SECTION_META[section.kind];
  const Icon = meta.icon;
  return (
    <>
      <div className="flex items-center gap-2 mb-2 pr-8">
        <Icon className={cn("h-4 w-4 shrink-0", meta.iconClass)} aria-hidden />
        <h4 className="text-xs font-semibold uppercase tracking-wider text-foreground/80">
          {section.title}
        </h4>
      </div>
      <Paragraphs
        paragraphs={section.paragraphs}
        textClass={cn(compact ? "text-sm text-foreground/85" : "text-[0.95rem] text-foreground/90")}
        spacing={compact ? "space-y-2" : "space-y-2.5"}
        searchQuery={searchQuery}
      />
    </>
  );
}

/* ---------------- Main component ---------------- */

const LONG_THRESHOLD_CHARS = 600;

export function ExplanationBody({
  text,
  mcqId,
  source,
  compact = false,
  className,
  searchQuery = "",
  onMatchCountChange,
}: ExplanationBodyProps) {
  const parsed = useMemo(() => parseExplanation(text), [text]);

  const isLong =
    parsed.sections.length >= 2 || parsed.totalLength > LONG_THRESHOLD_CHARS;

  // Expand/collapse state (with global preference).
  const [expanded, setExpanded] = useState<boolean>(() => {
    const pref = getGlobalExpandPreference();
    if (pref !== null) return pref;
    return !isLong;
  });

  // Highlights state (per mcqId + source).
  const canHighlight = Boolean(mcqId && source);
  const [highlights, setHighlightsState] = useState<Set<string>>(() => {
    if (!mcqId || !source) return new Set();
    return getHighlights(mcqId, source);
  });

  // Re-hydrate highlights when the MCQ or source changes.
  useEffect(() => {
    if (!mcqId || !source) {
      setHighlightsState(new Set());
      return;
    }
    setHighlightsState(getHighlights(mcqId, source));
  }, [mcqId, source]);

  const toggleHighlight = canHighlight
    ? (id: string) => {
        setHighlightsState((prev) => {
          const next = new Set(prev);
          if (next.has(id)) next.delete(id);
          else next.add(id);
          if (mcqId && source) setHighlights(mcqId, source, next);
          return next;
        });
      }
    : null;

  const handleToggleExpanded = () => {
    setExpanded((prev) => {
      const next = !prev;
      setGlobalExpandPreference(next);
      return next;
    });
  };

  /* -------- Search match accounting -------- */
  const trimmedQuery = searchQuery.trim();
  const searchActive = trimmedQuery.length >= SEARCH_MIN_LEN;
  const effectiveQuery = searchActive ? trimmedQuery : "";

  // Per-block match counts (stable identity by block id).
  const matchByBlock = useMemo(() => {
    const map = new Map<string, number>();
    if (!searchActive) return map;
    if (parsed.lead) {
      map.set(parsed.lead.id, countMatches(parsed.lead.text, effectiveQuery));
    }
    for (const sec of parsed.sections) {
      const c = sec.paragraphs.reduce(
        (acc, p) => acc + countMatches(p, effectiveQuery),
        0
      );
      map.set(sec.id, c);
    }
    for (const para of parsed.body) {
      map.set(para.id, countMatches(para.text, effectiveQuery));
    }
    return map;
  }, [parsed, effectiveQuery, searchActive]);

  const totalMatches = useMemo(() => {
    if (!searchActive) return 0;
    let n = 0;
    for (const v of matchByBlock.values()) n += v;
    return n;
  }, [matchByBlock, searchActive]);

  // Report match count up.
  const lastReportedRef = useRef<number>(-1);
  useEffect(() => {
    if (!onMatchCountChange) return;
    if (lastReportedRef.current === totalMatches) return;
    lastReportedRef.current = totalMatches;
    onMatchCountChange(totalMatches);
  }, [totalMatches, onMatchCountChange]);

  if (!parsed.lead && parsed.sections.length === 0 && parsed.body.length === 0) {
    return (
      <p
        className={cn(
          "italic text-muted-foreground",
          compact ? "text-sm" : "text-base",
          className
        )}
      >
        No explanation available.
      </p>
    );
  }

  const bodyTextClass = compact
    ? "text-sm text-foreground/85"
    : "text-[0.95rem] text-foreground/90";
  const blockGap = compact ? "space-y-3.5" : "space-y-4";
  const paraGap = compact ? "space-y-2" : "space-y-3";

  // While searching, force-expand so hidden matches become visible.
  const showAllSections = searchActive || expanded || !isLong;
  const visibleSections = showAllSections ? parsed.sections : parsed.sections.slice(0, 1);
  const visibleBody =
    showAllSections
      ? parsed.body
      : parsed.sections.length === 0
      ? parsed.body.slice(0, 1)
      : [];
  const hiddenSectionCount = parsed.sections.length - visibleSections.length;
  const hiddenBodyCount = parsed.body.length - visibleBody.length;
  const totalHidden = hiddenSectionCount + hiddenBodyCount;

  const isDimmed = (blockId: string) =>
    searchActive && (matchByBlock.get(blockId) ?? 0) === 0;

  return (
    <div className={cn("max-w-prose", blockGap, className)}>
      {/* Lead callout — always-on emphasis + optional user highlight */}
      {parsed.lead && (
        <BlockShell
          blockId={parsed.lead.id}
          highlighted={highlights.has(parsed.lead.id)}
          onToggleHighlight={toggleHighlight}
          compact={compact}
          searchDimmed={isDimmed(parsed.lead.id)}
          baseClass="border-l-primary border-primary/20 bg-primary/5 ring-1 ring-primary/15"
        >
          <div className="flex items-start gap-2.5 pr-8">
            <Lightbulb
              className={cn(
                "shrink-0 text-primary mt-0.5",
                compact ? "h-4 w-4" : "h-[18px] w-[18px]"
              )}
              aria-hidden
            />
            <div className="min-w-0 flex-1">
              <div className="text-[10px] font-bold uppercase tracking-wider text-primary mb-1">
                Key takeaway
              </div>
              <p
                className={cn(
                  "leading-relaxed text-foreground font-medium",
                  compact ? "text-sm" : "text-[0.95rem]"
                )}
              >
                {effectiveQuery
                  ? highlightMatches(parsed.lead.text, effectiveQuery)
                  : parsed.lead.text}
              </p>
            </div>
          </div>
        </BlockShell>
      )}

      {/* Structured sections */}
      {visibleSections.map((section) => (
        <BlockShell
          key={section.id}
          blockId={section.id}
          highlighted={highlights.has(section.id)}
          onToggleHighlight={toggleHighlight}
          compact={compact}
          searchDimmed={isDimmed(section.id)}
          baseClass={SECTION_META[section.kind].accentClass}
        >
          <SectionContent
            section={section}
            compact={compact}
            searchQuery={effectiveQuery}
          />
        </BlockShell>
      ))}

      {/* Remaining unlabeled paragraphs (each its own highlightable block) */}
      {visibleBody.length > 0 && (
        <div className={paraGap}>
          {visibleBody.map((para) => (
            <BlockShell
              key={para.id}
              blockId={para.id}
              highlighted={highlights.has(para.id)}
              onToggleHighlight={toggleHighlight}
              compact={compact}
              searchDimmed={isDimmed(para.id)}
              baseClass="border-l-border bg-transparent"
            >
              <p className={cn("leading-relaxed whitespace-pre-wrap pr-8", bodyTextClass)}>
                {effectiveQuery
                  ? highlightMatches(para.text, effectiveQuery)
                  : para.text}
              </p>
            </BlockShell>
          ))}
        </div>
      )}

      {/* Expand / collapse control (hidden while search forces expansion) */}
      {isLong && !searchActive && (
        <div className="pt-1">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={handleToggleExpanded}
            aria-expanded={expanded}
            className="w-full sm:w-auto flex items-center gap-1.5 text-muted-foreground hover:text-foreground"
          >
            {expanded ? (
              <>
                <ChevronUp className="h-4 w-4" />
                Show less
              </>
            ) : (
              <>
                <ChevronDown className="h-4 w-4" />
                Show more
                {totalHidden > 0 && (
                  <span className="ml-1 text-xs text-muted-foreground/80">
                    (+{totalHidden} more)
                  </span>
                )}
              </>
            )}
          </Button>
        </div>
      )}
    </div>
  );
}
